import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import SEO from '../../components/SEO';
import Button from '../../components/ui/Button';
import CalendarGrid from './components/CalendarGrid';
import EventModal from './components/EventModal';
import EventFilters from './components/EventFilters';
import PermissionDenied from '../../components/ui/PermissionDenied';
import { usePermissions } from '../../hooks/usePermissions';
import { calendarService } from '../../services/calendarService';
import { projectService } from '../../services/projectService';
import { contractorService } from '../../services/contractorService';
import { getTeamMembers } from '../../services/teamService';
import { supabase } from '../../lib/supabase';

const Calendar = () => {
  const { user, loading: authLoading } = useAuth();
  const permissions = usePermissions();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [permissionError, setPermissionError] = useState('');
  const [dragDropError, setDragDropError] = useState('');
  const [filters, setFilters] = useState({
    eventType: 'all',
    projectId: 'all',
    contractorId: 'all',
    teamMemberId: 'all'
  });

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    }

    // Listen for openEventDetails event from other components
    const handleOpenEventDetails = async (event) => {
      const { eventId } = event?.detail || {};
      if (eventId) {
        try {
          const eventData = await calendarService?.getById(eventId);
          if (eventData) {
            setSelectedEvent(eventData);
            setIsEventModalOpen(true);
          }
        } catch (error) {
          console.error('Error loading event:', error);
        }
      }
    };

    window.addEventListener('openEventDetails', handleOpenEventDetails);

    return () => {
      window.removeEventListener('openEventDetails', handleOpenEventDetails);
    };
  }, [authLoading, user]);

  // Real-time subscription for calendar events
  useEffect(() => {
    if (!user) return;

    const channel = supabase?.channel('calendar_events_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calendar_events'
        },
        (payload) => {
          console.log('Calendar event change detected:', payload);
          loadData();
        }
      )?.subscribe();

    // Real-time subscription for contractor availability changes
    const contractorChannel = supabase?.channel('contractors_availability_changes')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contractors'
        },
        (payload) => {
          console.log('Contractor availability change detected:', payload);
          loadData();
        }
      )?.subscribe();

    // Real-time subscription for team member status changes
    const teamChannel = supabase?.channel('team_member_status_changes')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          console.log('Team member status change detected:', payload);
          loadData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
      supabase?.removeChannel(contractorChannel);
      supabase?.removeChannel(teamChannel);
    };
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventsData, projectsData, contractorsData, teamMembersData] = await Promise.all([
        calendarService?.getAll(),
        projectService?.getAll(),
        contractorService?.getAll(),
        getTeamMembers()
      ]);
      
      setEvents(eventsData || []);
      setProjects(projectsData || []);
      setContractors(contractorsData || []);
      setTeamMembers(teamMembersData || []);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    if (!permissions?.canCreate) {
      setPermissionError('You do not have permission to add events.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setSelectedEvent(null);
    setIsEventModalOpen(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!permissions?.canDelete) {
      setPermissionError('You do not have permission to delete events.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    setDeletingEvent(eventId);
    try {
      await calendarService?.delete(eventId);
      await loadData();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    } finally {
      setDeletingEvent(null);
    }
  };

  const handleEventModalClose = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
  };

  const handleEventModalSuccess = async () => {
    await loadData();
    handleEventModalClose();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleEventDrop = async (event, newStartDate, newEndDate) => {
    if (!permissions?.canEdit) {
      setDragDropError('You do not have permission to reschedule events.');
      setTimeout(() => setDragDropError(''), 4000);
      return;
    }
    try {
      await calendarService?.update(event?.id, {
        ...event,
        startDate: newStartDate,
        endDate: newEndDate
      });
      await loadData();
    } catch (error) {
      console.error('Error rescheduling event via drag-drop:', error);
      setDragDropError('Failed to reschedule event. Please try again.');
      setTimeout(() => setDragDropError(''), 4000);
    }
  };

  const filteredEvents = events?.filter(event => {
    if (filters?.eventType !== 'all' && event?.eventType !== filters?.eventType) return false;
    if (filters?.projectId !== 'all' && event?.projectId !== filters?.projectId) return false;
    if (filters?.contractorId !== 'all' && event?.contractorId !== filters?.contractorId) return false;
    if (filters?.teamMemberId !== 'all' && event?.teamMemberId !== filters?.teamMemberId) return false;
    return true;
  });

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate?.setMonth(currentDate?.getMonth() + direction);
    } else if (viewMode === 'week') {
      newDate?.setDate(currentDate?.getDate() + (direction * 7));
    } else {
      newDate?.setDate(currentDate?.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="main-content">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading calendar...</p>
            </div>
          </div>
        </main>
        <QuickAddMenu />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Project Calendar — ADU Construction Schedule & Deadlines"
        description="View and manage your ADU construction schedule. Track project milestones, permit deadlines, contractor appointments, and team events in one unified calendar."
        canonical="/calendar"
        noIndex={true}
        keywords="ADU calendar, construction schedule, project milestones, permit deadlines, contractor appointments, team availability"
      />
      <Header />
      <main className="main-content">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Calendar</h1>
                <p className="text-muted-foreground">
                  Manage team availability, contractor bookings, project milestones, and permit inspections
                </p>
              </div>
              {permissions?.canCreate && (
                <Button
                  iconName="Plus"
                  onClick={handleCreateEvent}
                  size="lg"
                >
                  Add Event
                </Button>
              )}
            </div>
          </div>

          {/* Permission Denied Banner */}
          {(permissionError || dragDropError) && (
            <div className="mb-4">
              <PermissionDenied message={permissionError || dragDropError} />
            </div>
          )}

          {/* Filters */}
          <EventFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            projects={projects}
            contractors={contractors}
            teamMembers={teamMembers}
          />

          {/* Calendar Controls */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  onClick={() => navigateDate(-1)}
                />
                <div className="text-center min-w-48">
                  <h3 className="font-semibold text-lg text-foreground">
                    {currentDate?.toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric',
                      ...(viewMode === 'day' && { day: 'numeric' })
                    })}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  onClick={() => navigateDate(1)}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToToday}
                >
                  Today
                </Button>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === 'month' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('month')}
                  >
                    Month
                  </Button>
                  <Button
                    variant={viewMode === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('week')}
                  >
                    Week
                  </Button>
                  <Button
                    variant={viewMode === 'day' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('day')}
                  >
                    Day
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="grid grid-cols-7 gap-4 mb-6">
                  {[1, 2, 3, 4, 5, 6, 7]?.map((i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-4">
                  {[...Array(35)]?.map((_, i) => (
                    <div key={i} className="h-24 bg-gray-100 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Calendar Grid */}
              <CalendarGrid
                events={filteredEvents}
                viewMode={viewMode}
                currentDate={currentDate}
                onEventClick={handleEditEvent}
                onEventDelete={handleDeleteEvent}
                deletingEvent={deletingEvent}
                onEventDrop={handleEventDrop}
              />

              {/* Event Legend */}
              <div className="mt-6 bg-card rounded-xl shadow-sm border border-border p-4">
                <h3 className="font-semibold text-foreground mb-3">Event Types</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-success"></div>
                    <span className="text-sm text-muted-foreground">Team Availability</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500"></div>
                    <span className="text-sm text-muted-foreground">Contractor Bookings</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-purple-500"></div>
                    <span className="text-sm text-muted-foreground">Project Milestones</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-warning"></div>
                    <span className="text-sm text-muted-foreground">Permit Inspections</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <QuickAddMenu />
      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={handleEventModalClose}
        onSuccess={handleEventModalSuccess}
        event={selectedEvent}
        projects={projects}
        contractors={contractors}
        teamMembers={teamMembers}
      />
    </div>
  );
};

export default Calendar;